import { BuilderStyle, DataItem } from '@wix/document-services-types';
import { EditorMethodArgs } from '../../types';

import { PrivateAPINamespace } from '../PrivateAPINamespace';
import {
  ExternalPanelsAPIErrorCode,
  createEditorPlatformPrivateAPIError,
} from './errors';
import { InputsNamespace } from '../Inputs';

/**
 * @deprecated !!!
 */
export class ExternalPanelsNamespace extends PrivateAPINamespace {
  static readonly namespace = 'externalPanels';

  #inputs = new InputsNamespace(this.hostType, this.ps);

  private editorPointerAPI = (() => {
    if (!this.ps.platformPackagesBridge.EditorPointerAPI) {
      throw createEditorPlatformPrivateAPIError(
        ExternalPanelsAPIErrorCode.EditorPointerAPINotProvided,
      ).withHostType(this.hostType);
    }

    return this.ps.platformPackagesBridge.EditorPointerAPI;
  })();

  private async getExternalPanelsPrivateAPI() {
    return this.ps.hostAPI.getExternalPanelsPrivateAPI?.();
  }

  private async getSingleSelectedOrThrow() {
    return this.ps.hostAPI.editor.selection.getSingleSelectedOrThrow();
  }

  private async getComponentReferences() {
    const editorPointer = await this.getSingleSelectedOrThrow();
    const compRef = this.editorPointerAPI.getDocumentPointer(editorPointer);
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer);

    return { compRef, editorPointer, innerElementPath };
  }

  async getData() {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.getData) {
      return externalPanelsPrivateAPI.getData();
    }

    const { compRef } = await this.getComponentReferences();

    return this.ps.ds.components.data.get(compRef);
  }

  async setData(newData: DataItem) {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.setData) {
      return externalPanelsPrivateAPI.setData(newData);
    }

    const { compRef } = await this.getComponentReferences();

    this.ps.ds.components.data.update(compRef, newData);
  }

  async getStyle() {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.getStyle) {
      return externalPanelsPrivateAPI.getStyle();
    }

    const { compRef, editorPointer, innerElementPath } =
      await this.getComponentReferences();

    if (this.editorPointerAPI.isInnerElement(editorPointer)) {
      return this.ps.ds.components.innerElements.cssStyle.get(
        compRef,
        innerElementPath ?? [],
      );
    } else {
      return this.ps.ds.components.cssStyle.get(compRef);
    }
  }

  async setStyle(newStyle: Partial<BuilderStyle>) {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.setStyle) {
      return externalPanelsPrivateAPI.setStyle(newStyle);
    }

    const { compRef, editorPointer, innerElementPath } =
      await this.getComponentReferences();

    if (this.editorPointerAPI.isInnerElement(editorPointer)) {
      this.ps.ds.components.innerElements.cssStyle.update(
        compRef,
        innerElementPath ?? [],
        newStyle as Partial<BuilderStyle>,
      );
    } else {
      this.ps.ds.components.cssStyle.update(
        compRef,
        newStyle as Partial<BuilderStyle>,
      );
    }
  }

  async getTheme() {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.getTheme) {
      return externalPanelsPrivateAPI.getTheme();
    }

    const fonts = this.ps.ds.theme.fonts.getMap();
    const colors = this.ps.ds.theme.colors.getAll();

    return { fonts, colors, localImplementation: true };
  }

  async selectMedia(...options: EditorMethodArgs<'openMediaPanel'>) {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.selectMedia) {
      // @ts-expect-error deprecated
      return externalPanelsPrivateAPI.selectMedia(...options);
    }

    return this.#inputs.openMediaPanel(...options);
  }

  async selectLink(...options: EditorMethodArgs<'openLinkPanel'>) {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.selectLink) {
      // @ts-expect-error deprecated
      return externalPanelsPrivateAPI.selectLink(...options);
    }

    return this.#inputs.openLinkPanel(...options);
  }

  async selectColor(...options: EditorMethodArgs<'openColorPicker'>) {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.selectColor) {
      // @ts-expect-error deprecated
      return externalPanelsPrivateAPI.selectColor(...options);
    }

    return this.#inputs.openColorPicker(...options);
  }

  async translate(key: string | string[], values?: Record<string, any>) {
    const externalPanelsPrivateAPI = await this.getExternalPanelsPrivateAPI();

    if (externalPanelsPrivateAPI?.translate) {
      return externalPanelsPrivateAPI.translate(key, values);
    }

    return null;
  }
}
